import isEqual from 'react-fast-compare';

import type {FileApiModel} from '@yourcoach/shared/api/media/file';
import type {EditionApiModel as Edition} from '@yourcoach/shared/api/program';
import type {Expand} from '@yourcoach/shared/api/types';

import {pollAsync} from '@yourcoach/async-utils';
import {fileStore} from '@yourcoach/shared/api/media/file';
import {membershipStore} from '@yourcoach/shared/api/membership';
import {editionStore, programStore} from '@yourcoach/shared/api/program';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {
  getUniqueProgramSlug,
  isEditionIndividual,
} from '@yourcoach/shared/utils/entity/program';

import {expand as programExpand} from '@src/modules/programs/utils';

import type {ProgramT} from '..';

import {PROGRAM_AVATAR_UPLOADING_KEY} from '../Description';
import {PROGRAM_CONTRACT_UPLOADING_KEY} from '../Details';

const updateProgram = async (originalProgram: ProgramT, program: ProgramT) => {
  const originalLastEdition =
    originalProgram.editions[originalProgram.editions.length - 1];

  let lastEdition = (program.editions.slice()[
    program.editions.length - 1
  ] as ProgramT['editions'][0])!;

  type ScheduleItem =
    | (typeof lastEdition.conferences)[0]
    | (typeof lastEdition.materials)[0]
    | (typeof lastEdition.tasks)[0];

  if (
    program.status === 'draft' &&
    lastEdition.payment_plan &&
    lastEdition.payment_plan.processing === 'internal' &&
    lastEdition.payment_plan.total !== 0 &&
    program.expanded_coaches.some(coach => !coach.is_provider)
  ) {
    throw {
      code: 'isnt_provider_coaches',
    };
  }

  const shouldUpdateProgram = () => {
    const keys = ['title', 'description', 'coaches', 'avatar'];

    return keys.some(key => {
      let originalValue = originalProgram[key];
      let lastValue = program[key];

      if (key === 'coaches') {
        originalValue = Object.keys(originalValue).map(userId => ({
          _id: userId,
          portion: originalValue[userId].portion,
          role: originalValue[userId].role.toLowerCase(),
        }));

        lastValue = Object.keys(lastValue).map(userId => ({
          _id: userId,
          portion: lastValue[userId].portion,
          role: lastValue[userId].role.toLowerCase(),
        }));
      }

      return !isEqual(originalValue, lastValue);
    });
  };

  const shouldUpdateEdition = () => {
    const keys = [
      'group_size',
      'conferences',
      'tasks',
      'materials',
      'channels',
    ];

    if (!originalLastEdition) {
      return false;
    }

    return keys.some(
      key => !isEqual((originalLastEdition || {})[key], lastEdition[key]),
    );
  };

  const shouldCreateNewEdition = () => {
    const keys = [
      'duration',
      'payment_plan',
      'contract',
      'private_data_request',
      'client_conferences',
      'is_autorestarting',
    ];

    if (!originalLastEdition) {
      return true;
    }

    const lastEditionPaymentPlan =
      lastEdition.payment_plan && lastEdition.payment_plan.total
        ? lastEdition.payment_plan
        : null;

    return keys.some(key => {
      const originalValue = (originalLastEdition || {})[key];

      let lastValue = lastEdition[key];

      if (key === 'payment_plan') {
        lastValue = lastEditionPaymentPlan;
      }

      return !isEqual(originalValue, lastValue);
    });
  };

  let updatedProgram: ProgramT | undefined;

  if (shouldUpdateProgram()) {
    const programData: Pick<ProgramT, 'coaches' | 'description' | 'title'> & {
      avatar_id?: ProgramT['avatar_id'];
      expand?: Expand;
      slug?: ProgramT['slug'];
    } = {
      coaches: program.coaches,
      description: program.description,
      expand: programExpand,
      title: program.title,
    };

    let avatarId: null | string = null;

    if (program.status === 'draft') {
      const slug = await getUniqueProgramSlug(
        program.title,
        currentUserStore.user!._id,
      );

      programData.slug = slug;
    }

    if (program.avatar) {
      if ((program.avatar as FileApiModel)._id) {
        avatarId = (program.avatar as FileApiModel)._id;
      } else {
        const uploading = fileStore.uploading.get(PROGRAM_AVATAR_UPLOADING_KEY);

        if (uploading) {
          if (uploading.inProgress) {
            await pollAsync(() => uploading.inProgress === false);
          }

          if (uploading.success && uploading.file) {
            avatarId = uploading.file._id;
          } else if (uploading.error) {
            const file = await fileStore.upload(
              program.avatar as unknown as File,
            );

            avatarId = file._id;
          }
        }
      }
    }

    programData.avatar_id = avatarId;

    updatedProgram = (await programStore.update(
      program,
      programData,
    )) as ProgramT;
  }

  const filterSchedule = (item: ScheduleItem) =>
    item.day <= lastEdition.duration - 1;

  if (shouldCreateNewEdition()) {
    const newEditionData: Pick<
      Edition,
      | 'channels'
      | 'client_conferences'
      | 'conferences'
      | 'duration'
      | 'group_size'
      | 'is_autorestarting'
      | 'payment_plan'
      | 'private_data_request'
      | 'program_id'
      | 'tasks'
      | 'title'
    > & {
      contract_id?: Edition['contract_id'];
      expand?: Expand;
      materials: Edition['materials'];
    } = {
      channels: [],
      client_conferences: lastEdition.client_conferences,
      conferences: lastEdition.conferences
        .filter(filterSchedule)
        .map(conference => {
          const copy = {...conference};

          // @ts-expect-error: we can delete this
          delete copy.repeat;

          return copy;
        }),
      duration: lastEdition.duration,
      expand: {
        edition: ['contract_id', 'materials.file_ids'],
      },
      group_size: lastEdition.group_size,
      is_autorestarting: lastEdition.is_autorestarting,
      materials: lastEdition.materials.filter(filterSchedule).map(item => {
        const material = {...item};

        // @ts-expect-error: we can delete this
        delete material.files;

        return material;
      }),
      payment_plan:
        lastEdition.payment_plan && lastEdition.payment_plan.schedule.length
          ? lastEdition.payment_plan
          : null,
      private_data_request: lastEdition.private_data_request,
      program_id: program._id,
      tasks: lastEdition.tasks.filter(filterSchedule).map(task => {
        const copy = {...task};

        // @ts-expect-error: we can delete this
        delete copy.edition_uuid;
        // @ts-expect-error: we can delete this
        delete copy.coach_attachments;

        return copy;
      }),
      title: `v${program.editions.length + 1}`,
    };

    let contractId: null | string = null;

    if (lastEdition.contract) {
      if ((lastEdition.contract as FileApiModel)._id) {
        contractId = (lastEdition.contract as FileApiModel)._id;
      } else {
        const uploading = fileStore.uploading.get(
          PROGRAM_CONTRACT_UPLOADING_KEY,
        );

        if (uploading) {
          if (uploading.inProgress) {
            await pollAsync(() => uploading.inProgress === false);
          }

          if (uploading.success && uploading.file) {
            contractId = uploading.file._id;
          } else if (uploading.error) {
            const file = await fileStore.upload(
              lastEdition.contract as unknown as File,
            );

            contractId = file._id;
          }
        }
      }
    }

    newEditionData.contract_id = contractId;

    let edition = ((await editionStore.create(
      // @ts-expect-error: TODO: Fix this
      newEditionData,
    )) as ProgramT['editions'][0])!;

    if (program.status === 'active' && edition.coach_ids.length === 1) {
      edition = ((await editionStore.sign(edition, {
        sign: currentUserStore.user!.name,
      })) as ProgramT['editions'][0])!;

      if (edition.status === 'active' && isEditionIndividual(edition)) {
        await membershipStore.coach.moveToEdition({
          edition_id: edition._id,
        });
      }
    }

    await programStore.update(program, {
      edition_ids: [...program.edition_ids, edition._id],
      expand: programExpand,
    });

    lastEdition = {
      ...lastEdition,
      ...edition,
    };
  } else if (shouldUpdateEdition()) {
    const editionData: Pick<
      Edition,
      '_id' | 'conferences' | 'revision' | 'tasks'
    > & {
      channels?: Edition['channels'];
      contract_id?: Edition['contract_id'];
      expand?: Expand;
      group_size?: Edition['group_size'];
      materials: Edition['materials'];
    } = {
      _id: lastEdition._id,
      channels: lastEdition.channels,
      conferences: lastEdition.conferences
        .filter(filterSchedule)
        .map(conference => {
          const copy = {...conference};

          // @ts-expect-error: we can delete this
          delete copy.repeat;

          return copy;
        }),
      expand: {
        edition: ['contract_id', 'materials.file_ids'],
      },
      group_size: lastEdition.group_size,
      materials: lastEdition.materials.filter(filterSchedule).map(item => {
        const material = {...item};

        // @ts-expect-error: we can delete this
        delete material.files;

        return material;
      }),
      revision: lastEdition.revision,
      tasks: lastEdition.tasks.filter(filterSchedule).map(task => {
        const copy = {...task};

        // @ts-expect-error: we can delete this
        delete copy.edition_uuid;
        // @ts-expect-error: we can delete this
        delete copy.coach_attachments;

        return copy;
      }),
    };

    // editing by co-coach use case
    if (program.user_id !== currentUserStore.user!._id) {
      delete editionData.group_size;
      delete editionData.channels;
    }

    // use the latest revision
    if (program.coach_ids.length > 1) {
      const fetchedLastEdition = await editionStore.fetch({
        _id: lastEdition._id,
      });

      if (fetchedLastEdition!.revision !== lastEdition.revision) {
        editionData.revision = fetchedLastEdition!.revision;
      }
    }

    (await editionStore.update(
      lastEdition,
      editionData,
    )) as ProgramT['editions'][0];
  }

  return updatedProgram || program;
};

export default updateProgram;
